import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/design/design/node_modules/@primer/gatsby-theme-doctocat/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`Please read and follow these guidelines carefully when writing documentation, so that as many people as possible can benefit from Primer.`}</p>
    <h2>{`Primer documentation principles`}</h2>
    <h3>{`Concise but friendly`}</h3>
    <p>{`A large proportion of readers want to find an answer that helps them complete a task, so don't waste their time with unnecessary words. That doesn't mean talking like a robot though: a sprinkling of humor is fine as long as it doesn't make the documentation harder to parse or distract from instructions.`}</p>
    <h3>{`Universally understood`}</h3>
    <p>{`Avoid using phrases or referencing examples that are only familiar internally at GitHub. Assume readers are either members of the public or new to GitHub.`}</p>
    <h3>{`Production quality`}</h3>
    <p>{`Code examples should promote what we'd like to see used in production. People copy and paste code examples as a starting point for building interfaces, and/or reference the guidelines for examples of correct implementation. While examples might be simpler than what we'd use in production, the code should promote best practices and follow our principles and accessibility standards.`}</p>
    <h2>{`Audience`}</h2>
    <p>{`Primer documentation is primarily aimed at GitHub designers and engineers at various levels of experience, but folks in other areas will also benefit from and use the guidelines.`}</p>
    <h3>{`Assumed knowledge`}</h3>
    <p><strong parentName="p">{`Do:`}</strong></p>
    <ul>
      <li parentName="ul">{`In design docs, assume the reader knows basic design concepts and principles, such as the need for consistency, and terms such as “white space” or “scale”.`}</li>
      <li parentName="ul">{`When referring to GitHub-specific terminology, link to a glossary of terms, or another document where the reader can learn more.`}</li>
      <li parentName="ul">{`When referring to terms and ideas the reader may want to know more about, link to authoritative sources, such as `}<a parentName="li" {...{
          "href": "https://developer.mozilla.org/en-US/"
        }}>{`MDN`}</a>{` and the `}<a parentName="li" {...{
          "href": "https://www.w3.org/"
        }}>{`W3C`}</a>{`.`}</li>
      <li parentName="ul">{`In non-engineering docs, use code examples as necessary to ensure consistency and appropriate usage, but don't assume expertise in any programming language.`}</li>
    </ul>
    <p><strong parentName="p">{`Don’t:`}</strong></p>
    <ul>
      <li parentName="ul">{`Don’t assume the reader knows about internal GitHub terminology.`}</li>
    </ul>
    <h2>{`Voice and tone`}</h2>
    <ul>
      <li parentName="ul">{`Write as you speak, then tidy it up.`}</li>
      <li parentName="ul">{`Write affirmative sentences wherever possible. `}</li>
      <li parentName="ul">{`Be clear and concise. `}</li>
      <li parentName="ul">{`Use `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Imperative_mood"
        }}>{`imperative mood`}</a>{`, so that instructions are clear. `}</li>
      <li parentName="ul">{`Remove unnecessary words, like adverbs, and keep sentences and paragraphs short.`}</li>
      <li parentName="ul">{`Don’t sound like a robot or too formal.`}</li>
      <li parentName="ul">{`Don’t use sarcasm or irony, they may not translate well.`}</li>
      <li parentName="ul">{`Avoid double negatives.`}</li>
      <li parentName="ul">{`Avoid using expressions like “easy”, “simply”, “quick”, “just”. `}</li>
      <li parentName="ul">{`Avoid idioms, they can be hard to understand and translate.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “As a general rule”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “As a rule of thumb”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Don't use the passive voice to avoid using the word “you” (but remove it if unnecessary).`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “Open Figma”, “Submit an issue if you find a bug”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “Figma can be opened”, “You can open Figma”, “If a bug is found, submit an issue”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Avoid the possessive "our", and avoid addressing Primer as "our design system”.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “In the Figma library…”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “In our Figma library…”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Use "we" consistently to refer to the Design Systems / Infrastructure team, not to GitHub as a whole.`}</li>
    </ul>
    <h2>{`Grammar and usage`}</h2>
    <p>{`Follow the `}<a parentName="p" {...{
        "href": "https://github.com/github/brand/blob/main/content.md"
      }}>{`GitHub content guidelines`}</a>{` (this link is only accessible to GitHub staff), in particular:`}</p>
    <ul>
      <li parentName="ul">{`Follow US spelling.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “Color palette”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “Colour palette”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Use sentence case for titles.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “Utility classes”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “Utility Classes”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Capitalize only `}<a parentName="li" {...{
          "href": "https://en.wikipedia.org/wiki/Proper_noun"
        }}>{`proper nouns`}</a>{` and `}<a parentName="li" {...{
          "href": "https://github.com/github/brand/blob/main/content.md#products-ecosystem-and-programs"
        }}>{`product names`}</a>{` (this link is only accessible to GitHub staff).`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “Open a pull request"`}</li>
          <li parentName="ul"><strong parentName="li">{`Don't:`}</strong>{` "Open a Pull Request"`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Avoid positional language.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “The list that follows”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “The list below”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Use contractions.`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`Do:`}</strong>{` “Don’t use this class”, “We’re happy to see you!”`}</li>
          <li parentName="ul"><strong parentName="li">{`Don’t:`}</strong>{` “Do not use this class”, “We are happy to see you.”`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Exclamation points are OK, in moderation.`}</li>
    </ul>
    <p>{`You may also refer to the content style guide for GitHub docs (accessible to GitHub staff only), in specific:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/docs-internal/blob/main/contributing/content-style-guide.md#keyboard-shortcuts"
        }}>{`Keyboard shortcuts`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/docs-internal/blob/main/contributing/content-style-guide.md#procedural-steps"
        }}>{`Procedural steps`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/docs-internal/blob/main/contributing/content-style-guide.md#procedural-steps"
        }}>{`Titles`}</a>{` (of articles)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/github/docs-internal/blob/main/contributing/content-style-guide.md#procedural-steps"
        }}>{`User interface elements`}</a></li>
    </ul>
    <h2>{`Images and other examples`}</h2>
    <p>{`Use this `}<a parentName="p" {...{
        "href": "https://www.figma.com/file/YQT3nlmvSdZRPBnkDXvTFk/Doctocat-content-template?node-id=0%3A1"
      }}>{`Figma template`}</a>{` (accessible to GitHub staff only) to create images that are consistent across Primer documentation.`}</p>
    <p>{`When creating images with example UI for docs make sure that:`}</p>
    <ul>
      <li parentName="ul">{`All documentation examples should be of real github.com examples`}</li>
      <li parentName="ul">{`Examples only include the most important part of the UI, and don't try to show the entire UI`}</li>
      <li parentName="ul">{`Use meaningful copy (for example, use "New issue" instead of "Button" as the text inside a button)`}</li>
    </ul>
    <h2>{`References`}</h2>
    <ul>
      <li parentName="ul">{`If mentioning or referring to other styles and documentation, always link to the source.`}</li>
      <li parentName="ul">{`Reference an existing guide by linking to it, rather than duplicating the content. However, if this makes the documentation harder to follow, consider providing that reference in the document itself (for example: spacing scale, abbreviations).`}</li>
    </ul>
    <h2>{`Publishing checklist`}</h2>
    <ul>
      <li parentName="ul">{`Spellcheck text with an automated tool`}</li>
      <li parentName="ul">{`Spellcheck UI text in image examples`}</li>
      <li parentName="ul">{`Ask someone else to proofread the document including images - they may catch something you missed`}</li>
      <li parentName="ul">{`Test all the links`}
        <ul parentName="li">
          <li parentName="ul">{`Indicate links that only work for GitHub staff (for example, "for GitHub staff only", or "only accessible to GitHub staff")`}</li>
        </ul>
      </li>
      <li parentName="ul">{`Verify there isn’t any private or sensitive information`}</li>
      <li parentName="ul">{`Verify that all images include an alt text`}</li>
    </ul>
    <h2>{`Documenting components`}</h2>
    <p>{`When documenting components, consider the core elements needed to convey its main purpose and proper usage within the UI.`}</p>
    <p>{`See our full `}<a parentName="p" {...{
        "href": "https://primer.style/contribute/documentation/"
      }}>{`documentation guidelines here`}</a>{`.`}</p>
    <h3>{`Types of components`}</h3>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Regular`}</strong>{`: Standard components used to build Primer UI. See `}<a parentName="li" {...{
          "href": "/components/action-list"
        }}>{`ActionList`}</a>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`Internal`}</strong>{`: Components used by other components that do not exist on their own. See `}<a parentName="li" {...{
          "href": "https://primer.style/react/Overlay"
        }}>{`Overlay`}</a>{`.`}</li>
      <li parentName="ul"><strong parentName="li">{`Behavioral`}</strong>{`: Components with no real anatomy or structure, rather behaviors. See `}<a parentName="li" {...{
          "href": "https://primer.style/react/Truncate"
        }}>{`Truncate`}</a>{`.`}</li>
    </ul>
    <h3>{`Component documentation structure`}</h3>
    <h4>{`Description`}</h4>
    <p>{`The description appears directly under the title of the component. It may be automatically pulled from component-metadata, and can be edited as needed.`}</p>
    <ul>
      <li parentName="ul">{`What is this component, and how would you describe it to someone who has never used it before? This description should be concise and shouldn't include usage information, which is covered below this section.`}</li>
    </ul>
    <h4>{`Usage`}</h4>
    <ul>
      <li parentName="ul">{`When to use this component`}</li>
      <li parentName="ul">{`Best practices`}</li>
      <li parentName="ul">{`Do/Don't examples`}</li>
    </ul>
    <h4>{`Anatomy`}</h4>
    <ul>
      <li parentName="ul">{`Describe the elements that make up the component, including typography, spacing, and styles. This can also include sub components, if there are any. See `}<a parentName="li" {...{
          "href": "/components/action-list#anatomy"
        }}>{`Action list`}</a>{` as an example.`}</li>
    </ul>
    <h4>{`Options`}</h4>
    <ul>
      <li parentName="ul">{`Different sizes, typography options, layout variations, spacing, etc.`}</li>
      <li parentName="ul">{`Different applications of the component in context`}</li>
    </ul>
    <h4>{`Interactions`}</h4>
    <ul>
      <li parentName="ul">{`If there are specific behaviors or interactions that the consumer of this documentation should be aware of, it's important to document here. Is it clickable, or static, and does it have any specific interactions based on state? Are there multiple types of behavior, and when does each get applied? See `}<a parentName="li" {...{
          "href": "/components/autocomplete#sort-and-filter-behavior"
        }}>{`Autocomplete`}</a>{` as an example.`}</li>
    </ul>
    <h4>{`Accessibility`}</h4>
    <ul>
      <li parentName="ul">{`Any component-specific accessibility considerations should be documented. This could include keyboard navigation, touch targets, and any specific usage guidelines with regards to assistive technology. See `}<a parentName="li" {...{
          "href": "/components/segmented-control#accessibility"
        }}>{`Segmented control`}</a>{`.`}</li>
    </ul>
    <h4>{`Related components and patterns`}</h4>
    <h2>{`Review process`}</h2>
    <p>{`All pull requests for new and updated guidelines should be reviewed and approved by a member of the Design Infrastructure team. `}</p>
    <h2>{`Resources`}</h2>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.rewatch.com/video/o73hl5dh5hntp3xc-primer-docs-writing-tips"
        }}>{`Docs writing tips video`}</a>{` (only accessible to GitHub staff)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://github.com/primer/design/blob/master/CONTRIBUTING.md#design-guidelines-template"
        }}>{`Design guidelines template`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      